import {
  CloudDownloadOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../components/AppLayout';
import { PageHead } from '../components/PageHead';
import {
  api,
  apiDownload,
  type EventRow,
  type SrtConfigRow,
  type SrtMode,
} from '../lib/api';

const { Title, Text, Paragraph } = Typography;

const MODE_OPTIONS: { value: SrtMode; label: string; description: string }[] = [
  {
    value: 'listener',
    label: 'Listener',
    description: 'vMix in the office listens for an incoming SRT push from site.',
  },
  {
    value: 'caller',
    label: 'Caller',
    description: 'vMix in the office calls out to an SRT endpoint at site.',
  },
  { value: 'push', label: 'Push (alias for caller)', description: '' },
  { value: 'pull', label: 'Pull (alias for listener)', description: '' },
];

type FormValues = {
  name: string;
  mode: SrtMode;
  event_id: string | null;
  url: string;
  stream_id?: string | null;
  passphrase?: string | null;
  latency_ms?: number | null;
  notes?: string | null;
};

async function copy(text: string, label: string) {
  await navigator.clipboard.writeText(text);
  message.success(`Copied ${label}`);
}

function CopyField({ value, label }: { value: string | null | undefined; label: string }) {
  if (!value) return <Text type="secondary">—</Text>;
  return (
    <Space size="small">
      <code style={{ fontSize: 12 }}>{value}</code>
      <Button
        size="small"
        type="text"
        icon={<CopyOutlined />}
        onClick={() => copy(value, label)}
      />
    </Space>
  );
}

function PassphraseCell({ value }: { value: string | null }) {
  const [shown, setShown] = useState(false);
  if (!value) return <Text type="secondary">—</Text>;
  return (
    <Space size="small">
      <code style={{ fontSize: 12 }}>{shown ? value : '•'.repeat(Math.min(value.length, 12))}</code>
      <Button
        size="small"
        type="text"
        icon={shown ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        onClick={() => setShown((s) => !s)}
      />
      <Button
        size="small"
        type="text"
        icon={<CopyOutlined />}
        onClick={() => copy(value, 'passphrase')}
      />
    </Space>
  );
}

export function SrtScene() {
  const [rows, setRows] = useState<SrtConfigRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SrtConfigRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm<FormValues>();

  const load = async () => {
    setLoading(true);
    try {
      const [configs, allEvents] = await Promise.all([
        api<SrtConfigRow[]>('/api/srt-configs'),
        api<EventRow[]>('/api/events'),
      ]);
      setRows(configs);
      setEvents(allEvents);
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to load SRT configs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ mode: 'listener', event_id: null });
    setModalOpen(true);
  };

  const openEdit = (row: SrtConfigRow) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      mode: row.mode,
      event_id: row.event_id,
      url: row.url,
      stream_id: row.stream_id,
      passphrase: row.passphrase,
      latency_ms: row.latency_ms,
      notes: row.notes,
    });
    setModalOpen(true);
  };

  const onSave = async (values: FormValues) => {
    try {
      if (editing) {
        await api(`/api/srt-configs/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(values),
        });
      } else {
        await api('/api/srt-configs', {
          method: 'POST',
          body: JSON.stringify(values),
        });
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Save failed');
    }
  };

  const remove = async (id: string) => {
    try {
      await api(`/api/srt-configs/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Delete failed');
    }
  };

  const eventOptions = useMemo(
    () => [
      { value: null, label: 'Shared (not tied to an event)' },
      ...events.map((e) => ({ value: e.id, label: e.title })),
    ],
    [events],
  );

  const columns: ColumnsType<SrtConfigRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name: string, row) => (
        <div>
          <Text strong>{name}</Text>
          <div>
            <Tag color={row.mode === 'listener' || row.mode === 'pull' ? 'blue' : 'purple'}>
              {row.mode}
            </Tag>
            {row.event_title ? (
              <Tag>{row.event_title}</Tag>
            ) : row.event_id ? (
              <Tag>Event-attached</Tag>
            ) : (
              <Tag color="default">Shared</Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'SRT URL',
      dataIndex: 'url',
      render: (url: string) => <CopyField value={url} label="URL" />,
    },
    {
      title: 'Stream ID',
      dataIndex: 'stream_id',
      render: (sid: string | null) => <CopyField value={sid} label="stream ID" />,
    },
    {
      title: 'Passphrase',
      dataIndex: 'passphrase',
      render: (p: string | null) => <PassphraseCell value={p} />,
    },
    {
      title: 'Latency',
      dataIndex: 'latency_ms',
      width: 100,
      render: (l: number | null) => (l ? `${l} ms` : <Text type="secondary">—</Text>),
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_t, row) => (
        <Space>
          <Button
            size="small"
            icon={<CloudDownloadOutlined />}
            title="Download as Blackmagic streaming XML"
            onClick={() =>
              void apiDownload(
                `/api/srt-configs/${row.id}/blackmagic.xml`,
                `${row.name}-blackmagic.xml`,
              ).catch((err: { error?: string }) =>
                message.error(err.error ?? 'Download failed'),
              )
            }
          />
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm
            title="Delete this SRT config?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(row.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout>
      <PageHead title="SRT codes" />
      <Space
        align="center"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
      >
        <Title level={2} style={{ margin: 0 }}>
          SRT codes
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          New config
        </Button>
      </Space>
      <Paragraph type="secondary">
        Field staff can sign in here and copy SRT endpoints / passphrases for
        site-to-vMix bridges. Configs tagged with an event are shown alongside
        the event; "Shared" configs are persistent endpoints (e.g. the office
        vMix listener) re-used across productions.
      </Paragraph>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No SRT configs yet.' }}
        />
      </Card>

      <Modal
        title={editing ? `Edit ${editing.name}` : 'New SRT config'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="Save"
        width={680}
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please give the config a name' }]}
          >
            <Input placeholder='e.g. "Office vMix listener" or "Wembley OB → office"' autoFocus />
          </Form.Item>
          <Form.Item
            name="mode"
            label="Mode"
            rules={[{ required: true }]}
            extra={
              MODE_OPTIONS.find((o) => o.value === form.getFieldValue('mode'))
                ?.description
            }
          >
            <Select
              options={MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </Form.Item>
          <Form.Item name="event_id" label="Event">
            <Select options={eventOptions} />
          </Form.Item>
          <Form.Item
            name="url"
            label="SRT URL"
            rules={[{ required: true, message: 'The srt://… URL is required' }]}
          >
            <Input placeholder="srt://office.aotv.co.uk:9000" />
          </Form.Item>
          <Form.Item name="stream_id" label="Stream ID (optional)">
            <Input placeholder="e.g. wembley-cam-1" />
          </Form.Item>
          <Form.Item name="passphrase" label="Passphrase (optional)">
            <Input.Password />
          </Form.Item>
          <Form.Item name="latency_ms" label="Latency (ms, optional)">
            <InputNumber min={0} step={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
}
